/**
 * 预筛选错误内联反馈组件
 * 覆盖在图片上方显示错误信息，替代 Toast 提示
 * 基于"最小打扰"设计原则
 *
 * @see Requirements 2.1, 2.2, 2.3, 2.4
 */

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  AlertTriangle,
  Info,
  RefreshCw,
  ImagePlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  PrescreenErrorCategory,
  PrescreenErrorMessage,
} from "@/types/prescreen";
import { cn } from "@/lib/utils";

interface PrescreenErrorOverlayProps {
  /** 错误分类 */
  category: PrescreenErrorCategory | null;
  /** 错误消息 */
  message: PrescreenErrorMessage | null;
  /** 重试回调 - 重新上传同一张图 */
  onRetry: () => void;
  /** 重新选择回调 - 打开文件选择器 */
  onReselect: () => void;
  /** 是否显示 */
  visible: boolean;
  /** 自定义类名 */
  className?: string;
}

/**
 * 根据图标类型获取对应的图标组件
 */
function getIcon(iconType: "warning" | "error" | "info") {
  switch (iconType) {
    case "error":
      return <AlertCircle className="h-8 w-8 text-red-500" />;
    case "warning":
      return <AlertTriangle className="h-8 w-8 text-yellow-500" />;
    case "info":
    default:
      return <Info className="h-8 w-8 text-blue-500" />;
  }
}

/**
 * 根据图标类型获取背景色
 */
function getBackgroundClass(iconType: "warning" | "error" | "info") {
  switch (iconType) {
    case "error":
      return "bg-red-500/10 border-red-500/30";
    case "warning":
      return "bg-yellow-500/10 border-yellow-500/30";
    case "info":
    default:
      return "bg-blue-500/10 border-blue-500/30";
  }
}

/**
 * 预筛选错误内联反馈组件
 */
export const PrescreenErrorOverlay: React.FC<PrescreenErrorOverlayProps> = ({
  category,
  message,
  onRetry,
  onReselect,
  visible,
  className,
}) => {
  const { t } = useTranslation("assessments");

  if (!visible || !message || !category) {
    return null;
  }

  const handleAction = () => {
    if (message.canRetry) {
      onRetry();
    } else {
      onReselect();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className={cn(
            "absolute inset-0 z-20 flex items-center justify-center",
            "bg-black/60 backdrop-blur-sm rounded-sm",
            className
          )}
          data-testid="prescreen-error-overlay"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
            className={cn(
              "flex flex-col items-center text-center p-6 rounded-lg",
              "border max-w-[280px]",
              getBackgroundClass(message.iconType)
            )}
          >
            {/* 图标 */}
            <div className="mb-3">{getIcon(message.iconType)}</div>

            {/* 标题 */}
            <h4
              className="text-sm font-semibold text-white mb-1"
              data-testid="error-title"
            >
              {message.title}
            </h4>

            {/* 描述 */}
            <p
              className="text-xs text-white/80 mb-4"
              data-testid="error-description"
            >
              {message.description}
            </p>

            {/* 操作按钮 */}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAction}
              className="bg-white/90 hover:bg-white text-gray-900 font-medium"
              data-testid="error-action-button"
            >
              {message.canRetry ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1.5" />
                  {message.actionHint}
                </>
              ) : (
                <>
                  <ImagePlus className="h-4 w-4 mr-1.5" />
                  {message.actionHint}
                </>
              )}
            </Button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PrescreenErrorOverlay;
